// packages/components/src/doctor-ai/PatientVitals.tsx
import React from 'react';
import type { Patient } from '@dopomogai/types';
import { Badge } from '@dopomogai/ui';
import { Cake, Droplet, ShieldAlert, User, Ruler } from 'lucide-react';

interface VitalInfoProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const VitalInfo: React.FC<VitalInfoProps> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="text-slate-500 mt-0.5">{icon}</div>
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      <div className="text-md font-bold text-slate-800">{value}</div>
    </div>
  </div>
);

export const PatientVitals: React.FC<{ patient: Patient | null }> = ({ patient }) => {
  // [KEY FIX] Add a guard clause. If patient data is not yet available, render nothing.
  // This prevents the "cannot read properties of undefined" crash.
  if (!patient) {
    return null; 
  }

  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-4">
        <VitalInfo icon={<Cake size={20} />} label="Age" value={`${patient.age} years`} />
        <VitalInfo icon={<User size={20} />} label="Sex" value={patient.sex} />
        <VitalInfo icon={<Droplet size={20} />} label="Blood Type" value={patient.bloodType} />
        <VitalInfo icon={<Ruler size={20} />} label="BMI" value={patient.bmi} />
      </div>
      <div className="border-t-2 border-dashed border-slate-200 mt-4 pt-3">
         <VitalInfo
          icon={<ShieldAlert size={20} className="text-red-600"/>}
          label="CRITICAL ALLERGIES"
          value={
            <div className="flex flex-wrap gap-1.5 mt-1">
              {/* This line is now safe because we've already checked that `patient` exists */}
              {patient.allergies.length > 0 && patient.allergies[0].toLowerCase() !== 'none' ? (
                patient.allergies.map(allergy => (
                  <Badge key={allergy} text={allergy} accentColor="red" />
                ))
              ) : (
                <Badge text="None Reported" accentColor="green" />
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};